import { useEffect, useRef } from "react";
import { renderInfoWindow } from "../utils";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

type LegacyFacility = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    address: string;
    coordinates: [number, number];
    guardianNames: string[];
};

const useLegacyFacilitiesMarker = (
    map: google.maps.Map | undefined,
    facilities: LegacyFacility[],
    siteTypes: string[],
) => {
    const markersRef = useRef<google.maps.Marker[]>([]);
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const siteTypesRef = useRef(siteTypes);
    siteTypesRef.current = siteTypes;

    useEffect(() => {
        if (!map) return;

        const isLegacyFacilities = siteTypesRef.current.includes("legacy-facilities");

        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];
        clustererRef.current?.clearMarkers();
        clustererRef.current = null;

        if (!isLegacyFacilities) return;

        markersRef.current = facilities.map((facility) => {
            const [lng, lat] = facility.coordinates;

            const marker = new google.maps.Marker({
                position: { lat, lng },
                clickable: true,
                icon: {
                    url: "/assets/mapicons/facility.png",
                    size: new google.maps.Size(24, 24),
                    scaledSize: new google.maps.Size(24, 24),
                    anchor: new google.maps.Point(12, 12),
                },
                optimized: true,
            });

            const contentString = `<div style="font-size: 14px; padding: 16px"><p><span style="font-weight: 500;">Facility Name:</span> ${facility.name}</p><p><span style="font-weight: 500;">Facility Client Name:</span> ${facility.clientName}</p></div>`;
            const infowindow = renderInfoWindow(contentString, facility.name);

            marker.addListener("click", () => {
                infowindow.open({ map, anchor: marker });
            });

            marker.setMap(map);
            return marker;
        });

        clustererRef.current = new MarkerClusterer({ map, markers: markersRef.current });
    }, [facilities, map, siteTypes]);
};

export default useLegacyFacilitiesMarker;
export type { LegacyFacility };
